// Custom Process Flow Node Component
// Renders AIAG-standard process flow symbols with properties

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import clsx from 'clsx';
import {
    Cog,
    Search,
    ArrowRight,
    Database,
    Clock,
    GitBranch,
    Circle,
    Layers,
    BarChart3,
    Shield,
    AlertTriangle,
    Plus
} from 'lucide-react';
import {
    ProcessStepData,
    ProcessStepType,
    SpecialCharacteristic,
    STEP_TYPE_COLORS
} from '../../types/processFlow';

// Icon mapping for step types
const STEP_ICONS: Record<ProcessStepType, React.ReactNode> = {
    [ProcessStepType.OPERATION]: <Cog size={16} />,
    [ProcessStepType.INSPECTION]: <Search size={16} />,
    [ProcessStepType.TRANSPORT]: <ArrowRight size={16} />,
    [ProcessStepType.STORAGE]: <Database size={16} />,
    [ProcessStepType.DELAY]: <Clock size={16} />,
    [ProcessStepType.DECISION]: <GitBranch size={16} />,
    [ProcessStepType.START_END]: <Circle size={16} />,
    [ProcessStepType.SUBPROCESS]: <Layers size={16} />,
    [ProcessStepType.SPC_CONTROL]: <BarChart3 size={16} />,
    [ProcessStepType.POKA_YOKE]: <Shield size={16} />,
};

// Shape styles for different step types
const getShapeStyles = (type: ProcessStepType): string => {
    switch (type) {
        case ProcessStepType.OPERATION:
            return 'rounded-lg'; // Rectangle with rounded corners
        case ProcessStepType.INSPECTION:
            return 'rounded-xl rotate-45'; // Diamond (rotated square)
        case ProcessStepType.TRANSPORT:
            return 'rounded-r-full rounded-l-lg'; // Arrow shape
        case ProcessStepType.STORAGE:
            return 'rounded-t-lg rounded-b-none'; // Triangle-ish (visual)
        case ProcessStepType.DELAY:
            return 'rounded-r-full rounded-l-lg'; // D-shape
        case ProcessStepType.DECISION:
            return 'rounded-none'; // Diamond will be rendered separately
        case ProcessStepType.START_END:
            return 'rounded-full'; // Oval/rounded terminal
        case ProcessStepType.SUBPROCESS:
            return 'rounded border-double border-4'; // Double-lined rectangle
        case ProcessStepType.SPC_CONTROL:
            return 'rounded-lg border-dashed'; // Dashed for SPC
        case ProcessStepType.POKA_YOKE:
            return 'rounded-lg'; // Standard with icon
        default:
            return 'rounded-lg';
    }
};

// Enhanced connection handle component
const ConnectionHandle: React.FC<{
    type: 'source' | 'target';
    position: Position;
    id?: string;
    selected: boolean;
    color?: string;
    label?: string;
    style?: React.CSSProperties;
}> = ({ type, position, id, selected, color = 'slate', label, style }) => {
    const baseColor = color === 'green' ? 'bg-green-500' :
        color === 'red' ? 'bg-red-500' :
            color === 'blue' ? 'bg-blue-500' : 'bg-slate-400';

    return (
        <div className="relative">
            <Handle
                type={type}
                position={position}
                id={id}
                className={clsx(
                    'transition-all duration-200 !border-2 !border-white',
                    selected ? '!w-4 !h-4' : '!w-2 !h-2',
                    baseColor,
                    selected && 'animate-pulse shadow-lg'
                )}
                style={style}
            />
            {/* Connection label when selected */}
            {selected && label && (
                <div
                    className={clsx(
                        'absolute text-[8px] font-bold text-white px-1 rounded whitespace-nowrap pointer-events-none',
                        baseColor
                    )}
                    style={{
                        ...(position === Position.Top && { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: '4px' }),
                        ...(position === Position.Bottom && { top: '100%', left: '50%', transform: 'translateX(-50%)', marginTop: '4px' }),
                        ...(position === Position.Left && { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: '4px' }),
                        ...(position === Position.Right && { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: '4px' }),
                    }}
                >
                    {label}
                </div>
            )}
            {/* Plus icon indicator when selected */}
            {selected && (
                <div
                    className={clsx(
                        'absolute flex items-center justify-center pointer-events-none',
                        'w-3 h-3 rounded-full bg-white shadow-sm'
                    )}
                    style={{
                        ...(position === Position.Top && { top: -14, left: '50%', transform: 'translateX(-50%)' }),
                        ...(position === Position.Bottom && { bottom: -14, left: '50%', transform: 'translateX(-50%)' }),
                        ...(position === Position.Left && { left: -14, top: '50%', transform: 'translateY(-50%)' }),
                        ...(position === Position.Right && { right: -14, top: '50%', transform: 'translateY(-50%)' }),
                    }}
                >
                    <Plus size={8} className="text-blue-600" />
                </div>
            )}
        </div>
    );
};

const ProcessFlowNode: React.FC<NodeProps<ProcessStepData>> = ({
    data,
    selected,
    id: _id
}) => {
    const colors = STEP_TYPE_COLORS[data.type] || STEP_TYPE_COLORS[ProcessStepType.OPERATION];
    const isDiamond = data.type === ProcessStepType.DECISION || data.type === ProcessStepType.INSPECTION;

    // Special characteristic badge
    const renderCharacteristicBadge = () => {
        if (!data.specialCharacteristic || data.specialCharacteristic === SpecialCharacteristic.NONE) {
            return null;
        }

        const badges: Record<SpecialCharacteristic, { bg: string; text: string; label: string }> = {
            [SpecialCharacteristic.NONE]: { bg: '', text: '', label: '' },
            [SpecialCharacteristic.CC]: { bg: 'bg-red-500', text: 'text-white', label: 'CC' },
            [SpecialCharacteristic.SC]: { bg: 'bg-amber-500', text: 'text-white', label: 'SC' },
            [SpecialCharacteristic.STANDARD]: { bg: 'bg-blue-500', text: 'text-white', label: 'STD' },
        };

        const badge = badges[data.specialCharacteristic];
        return (
            <div className={clsx(
                'absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shadow-md z-10',
                badge.bg, badge.text
            )}>
                {badge.label}
            </div>
        );
    };

    // 4M indicator
    const render4MIndicator = () => {
        if (!data.element4M) return null;

        const indicators = {
            man: { icon: '👤', color: 'bg-purple-100 text-purple-700' },
            machine: { icon: '⚙️', color: 'bg-blue-100 text-blue-700' },
            material: { icon: '📦', color: 'bg-green-100 text-green-700' },
            method: { icon: '📋', color: 'bg-amber-100 text-amber-700' },
        };

        const indicator = indicators[data.element4M];
        return (
            <div className={clsx(
                'absolute -bottom-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center text-xs shadow-md z-10',
                indicator.color
            )}>
                {indicator.icon}
            </div>
        );
    };

    // Diamond shape for decision/inspection nodes
    if (isDiamond) {
        return (
            <div className="relative">
                {/* Top handle - input */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                    <ConnectionHandle
                        type="target"
                        position={Position.Top}
                        selected={selected || false}
                        label={selected ? "IN" : undefined}
                    />
                </div>

                {renderCharacteristicBadge()}
                {render4MIndicator()}

                <div
                    className={clsx(
                        'w-32 h-32 flex items-center justify-center transform rotate-45 transition-all',
                        'border-2 shadow-md',
                        selected && 'ring-2 ring-offset-2 ring-blue-500'
                    )}
                    style={{
                        backgroundColor: colors.bg,
                        borderColor: colors.border,
                    }}
                >
                    <div className="transform -rotate-45 text-center p-2">
                        <div className="flex items-center justify-center mb-1" style={{ color: colors.text }}>
                            {STEP_ICONS[data.type]}
                        </div>
                        {data.stepNumber && (
                            <div className="text-[10px] font-bold opacity-70" style={{ color: colors.text }}>
                                {data.stepNumber}
                            </div>
                        )}
                        <div className="text-xs font-medium leading-tight max-w-[80px]" style={{ color: colors.text }}>
                            {data.label}
                        </div>
                    </div>
                </div>

                {/* Bottom handle - output */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-20">
                    <ConnectionHandle
                        type="source"
                        position={Position.Bottom}
                        selected={selected || false}
                        color="blue"
                        label={selected ? "PASS" : undefined}
                    />
                </div>

                {/* Side handles for decision branches */}
                {data.type === ProcessStepType.DECISION && (
                    <>
                        <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                            <ConnectionHandle
                                type="source"
                                position={Position.Left}
                                id="left"
                                selected={selected || false}
                                color="red"
                                label={selected ? "SCRAP" : undefined}
                            />
                        </div>
                        <div className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 z-20">
                            <ConnectionHandle
                                type="source"
                                position={Position.Right}
                                id="right"
                                selected={selected || false}
                                color="green"
                                label={selected ? "REWORK" : undefined}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Standard rectangular nodes
    return (
        <div className="relative">
            {/* Top handle - input */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                <ConnectionHandle
                    type="target"
                    position={Position.Top}
                    selected={selected || false}
                    label={selected ? "IN" : undefined}
                />
            </div>

            {renderCharacteristicBadge()}
            {render4MIndicator()}

            <div
                className={clsx(
                    'min-w-[160px] max-w-[200px] px-4 py-3 transition-all',
                    'border-2 shadow-md',
                    getShapeStyles(data.type),
                    selected && 'ring-2 ring-offset-2 ring-blue-500',
                    data.type === ProcessStepType.SPC_CONTROL && 'border-dashed',
                    data.type === ProcessStepType.SUBPROCESS && 'border-double border-4'
                )}
                style={{
                    backgroundColor: colors.bg,
                    borderColor: colors.border,
                }}
            >
                {/* Header with step number and icon */}
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                        <span style={{ color: colors.text }}>
                            {STEP_ICONS[data.type]}
                        </span>
                        {data.stepNumber && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/50" style={{ color: colors.text }}>
                                {data.stepNumber}
                            </span>
                        )}
                    </div>

                    {/* Warning indicator for linked PFMEA items with high risk */}
                    {data.pfmeaItemIds && data.pfmeaItemIds.length > 0 && (
                        <div className="flex items-center gap-1 text-amber-600" title={`${data.pfmeaItemIds.length} linked failure modes`}>
                            <AlertTriangle size={12} />
                            <span className="text-[10px] font-medium">{data.pfmeaItemIds.length}</span>
                        </div>
                    )}
                </div>

                {/* Label */}
                <div className="text-sm font-medium leading-tight" style={{ color: colors.text }}>
                    {data.label}
                </div>

                {/* Equipment */}
                {data.equipment && (
                    <div className="text-[10px] mt-1 opacity-70" style={{ color: colors.text }}>
                        🔧 {data.equipment}
                    </div>
                )}

                {/* Cycle time */}
                {data.cycleTime && (
                    <div className="text-[10px] opacity-70" style={{ color: colors.text }}>
                        ⏱️ {data.cycleTime}s
                    </div>
                )}

                {/* Control method indicator */}
                {data.controlMethod && (
                    <div className="text-[10px] mt-1 px-1.5 py-0.5 rounded bg-white/50 inline-block" style={{ color: colors.text }}>
                        📋 {data.controlMethod}
                    </div>
                )}
            </div>

            {/* Bottom handle - output */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 z-20">
                <ConnectionHandle
                    type="source"
                    position={Position.Bottom}
                    selected={selected || false}
                    color="blue"
                    label={selected ? "OUT" : undefined}
                />
            </div>

            {/* Side handles for connecting branches (visible when selected) */}
            {selected && (
                <>
                    <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                        <ConnectionHandle
                            type="source"
                            position={Position.Left}
                            id="left"
                            selected={true}
                            color="slate"
                        />
                    </div>
                    <div className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 z-20">
                        <ConnectionHandle
                            type="source"
                            position={Position.Right}
                            id="right"
                            selected={true}
                            color="slate"
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default memo(ProcessFlowNode);

